import { FC, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useSelector } from 'react-redux';
// components
import PublicLayout from 'hoc/publicLayout/PublicLayout';
import AMBGLoader from 'shared/components/AMBGLoader/AMBGLoader';
import { MODULE, PERMISSION, ROUTES } from 'shared/constants/constant';
import LoginScreen from 'features/auth/login/LoginScreen';
import ForgotPasswordScreen from 'features/auth/forgot-password/ForgotPasswordScreen';
import ResetPasswordScreen from 'features/auth/reset-password/ResetPasswordScreen';
import VerifyAccountScreen from 'features/auth/verify-account/verifyAccount';
import Layout from 'hoc/layout/Layout';
import {
	User,
	UserFormScreen,
	Master,
	MasterForm,
	Mapping,
	Dashboard,
	MySales,
	SalesCreateChangeLog,
	SalesUpdatedChangeLog,
	MasterCreateChangeLog,
	IndustrySales,
	SubmissionSummary,
	MasterUpdatedChangeLog,
	MasterDeleteChangeLog,
	MySalesExcelScreen,
	MasterHistoryLog,
	MappingHistoryLog,
	MarketList,
	MySalesHistoryLog,
	MyProfile,
	ChangePassword,
	AccessDenied,
	NewsRoomScreen,
	NewsFormScreen,
	NewsInteractionsView,
	NewsDetailViewScreen,
	VotingScreen,
	VoteForm,
	VoteResult
} from 'hoc/asyncComponents';

// types/interface
import { IState } from 'shared/interface/state';
import ProtectedRoute from 'hoc/protectedRoute/ProtectedRoute';

const App: FC = () => {
	const isLoggedIn = useSelector((state: IState) => state.auth.isLoggedIn);
	if (isLoggedIn) {
		return (
			<Layout>
				<Suspense fallback={<AMBGLoader />}>
					<Routes>
						<Route path={ROUTES.DASHBOARD} element={<Dashboard />} />
						<Route path={ROUTES.MY_PROFILE} element={<MyProfile />} />
						<Route path={ROUTES.CHANGE_PASSWORD} element={<ChangePassword />} />
						<Route path={`${ROUTES.DETAIL_VIEW}/:newsId`} element={<NewsDetailViewScreen />} />

						<Route element={<ProtectedRoute module={MODULE.USER} permission={PERMISSION.LIST} />}>
							<Route path={ROUTES.USER} element={<User />} />
						</Route>
						<Route element={<ProtectedRoute module={MODULE.USER} permission={PERMISSION.ADD} />}>
							<Route path={ROUTES.ADD_USER} element={<UserFormScreen />} />
						</Route>
						<Route element={<ProtectedRoute module={MODULE.USER} permission={PERMISSION.EDIT} />}>
							<Route path={`${ROUTES.EDIT_USER}/:userId`} element={<UserFormScreen />} />
						</Route>

						<Route element={<ProtectedRoute module={MODULE.MASTER} permission={PERMISSION.LIST} />}>
							<Route path={ROUTES.MASTER} element={<Master />} />
							<Route path={ROUTES.MAPPING} element={<Mapping />} />
							<Route path={ROUTES.MAPPING_MARKETS} element={<MarketList />} />
						</Route>
						<Route element={<ProtectedRoute module={MODULE.MASTER} permission={PERMISSION.ADD} />}>
							<Route path={ROUTES.ADD_MASTER} element={<MasterForm />} />
						</Route>
						<Route element={<ProtectedRoute module={MODULE.MASTER} permission={PERMISSION.EDIT} />}>
							<Route path={`${ROUTES.EDIT_MASTER}/:companyId/:masterId`} element={<MasterForm />} />
						</Route>
						<Route element={<ProtectedRoute module={MODULE.MASTER} permission={PERMISSION.HISTORY} />}>
							<Route
								path={`${ROUTES.MASTER_HISTORY_LOG}/:companyId/:masterId`}
								element={<MasterHistoryLog />}
							/>
							<Route
								path={`${ROUTES.MAPPING_HISTORY_LOG}/:companyId`}
								element={<MappingHistoryLog />}
							/>
						</Route>

						<Route element={<ProtectedRoute module={MODULE.MY_SALES} permission={PERMISSION.LIST} />}>
							<Route path={ROUTES.MY_SALES} element={<MySales />} />
						</Route>
						<Route element={<ProtectedRoute module={MODULE.MY_SALES} permission={PERMISSION.ADD} />}>
							<Route path={ROUTES.ADD_MY_SALES} element={<MySalesExcelScreen />} />
						</Route>
						<Route element={<ProtectedRoute module={MODULE.MY_SALES} permission={PERMISSION.EDIT} />}>
							<Route path={ROUTES.EDIT_MY_SALES} element={<MySalesExcelScreen />} />
						</Route>
						<Route element={<ProtectedRoute module={MODULE.MY_SALES} permission={PERMISSION.HISTORY} />}>
							<Route
								path={`${ROUTES.MY_SALES_HISTORY_LOG}/:companyId/:mySalesId`}
								element={<MySalesHistoryLog />}
							/>
						</Route>

						<Route element={<ProtectedRoute module={MODULE.INDUSTRY_SALES} permission={PERMISSION.LIST} />}>
							<Route path={ROUTES.INDUSTRY_SALES} element={<IndustrySales />} />
						</Route>
						<Route path={ROUTES.SUBMISSION_SUMMARY} element={<SubmissionSummary />} />

						<Route element={<ProtectedRoute module={MODULE.NEWS_ROOM} permission={PERMISSION.LIST} />}>
							<Route path={ROUTES.NEWS_ROOM} element={<NewsRoomScreen />} />
						</Route>
						<Route element={<ProtectedRoute module={MODULE.NEWS_ROOM} permission={PERMISSION.ADD} />}>
							<Route path={ROUTES.ADD_NEWS} element={<NewsFormScreen />} />
						</Route>
						<Route element={<ProtectedRoute module={MODULE.NEWS_ROOM} permission={PERMISSION.EDIT} />}>
							<Route path={`${ROUTES.EDIT_NEWS}/:newsId`} element={<NewsFormScreen />} />
						</Route>
						<Route element={<ProtectedRoute module={MODULE.NEWS_ROOM} permission={PERMISSION.EDIT} />}>
							<Route path={`${ROUTES.VIEW_NEWS}/:newsId`} element={<NewsInteractionsView />} />
						</Route>

						<Route
							path={`${ROUTES.MASTER_CREATE_CHANGE_LOG}/:notificationId`}
							element={<MasterCreateChangeLog />}
						/>
						<Route
							path={`${ROUTES.MASTER_UPDATE_CHANGE_LOG}/:notificationId`}
							element={<MasterUpdatedChangeLog />}
						/>
						<Route
							path={`${ROUTES.MASTER_DELETE_CHANGE_LOG}/:notificationId`}
							element={<MasterDeleteChangeLog />}
						/>
						<Route
							path={`${ROUTES.SALES_CREATE_CHANGE_LOG}/:notificationId`}
							element={<SalesCreateChangeLog />}
						/>
						<Route
							path={`${ROUTES.SALES_UPDATE_CHANGE_LOG}/:notificationId`}
							element={<SalesUpdatedChangeLog />}
						/>

						<Route element={<ProtectedRoute module={MODULE.VOTING} permission={PERMISSION.LIST} />}>
							<Route path={ROUTES.VOTING} element={<VotingScreen />} />
						</Route>
						<Route element={<ProtectedRoute module={MODULE.VOTING} permission={PERMISSION.ADD} />}>
							<Route path={ROUTES.ADD_VOTING} element={<VoteForm />} />
						</Route>
						<Route element={<ProtectedRoute module={MODULE.VOTING} permission={PERMISSION.EDIT} />}>
							<Route path={`${ROUTES.VOTING_DETAILS}/:pollId`} element={<VoteResult />} />
						</Route>

						<Route path={ROUTES.ACCESS_DENIED} element={<AccessDenied />} />
						<Route path='*' element={<Navigate replace to={ROUTES.DASHBOARD} />} />
					</Routes>
				</Suspense>
			</Layout>
		);
	} else {
		return (
			<PublicLayout>
				<Routes>
					<Route path={ROUTES.LOGIN} element={<LoginScreen />} />
					<Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPasswordScreen />} />
					<Route path={ROUTES.RESET_PASSWORD} element={<ResetPasswordScreen />} />
					<Route path={ROUTES.VERIFY_ACCOUNT} element={<VerifyAccountScreen />} />
					<Route path='*' element={<Navigate replace to={ROUTES.LOGIN} />} />
				</Routes>
			</PublicLayout>
		);
	}
};

export default App;
